import React, { useState } from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import API from 'Vulture/lib/API.js';

// Import styles to match Library design
import styles from './SlideDeck.module.css';
import SlideDeckModal from './SlideDeckModal';
import SlideDeckTableView from './SlideDeckTable';
import SlideDeckSlidesTableView from './SlideDeckSlidesTable';
import InFolderScheduling from './InFolderScheduling';
import {
	getThumbnailPath,
	getSlideThumbnailURL,
} from './Chonky/lib';
import SlideModal from './SlideModal';
import {
	createSlideDeckSlideWithUpload,
} from 'Vulture/Content';
import {
	slideDeckDeleteValidation,
} from 'Vulture/SlideDeck';
import {
	getContentManagersByAccount,
} from 'Vulture/Users';
import {
	getSession,
	isContentManager,
} from 'Vulture/Session';
import {
	getFDValue,
} from 'Vulture/Helpers';
import {
	getURLSlideThumbnailPath,
	urlSlideFallbackIcon,
	URL_SLIDE_MAX_LENGTH,
} from './utils/slides';
import {
	hasGlobalScope,
	hasPermission,
} from 'Vulture/Permissions';

// Publishing a deck as a cross-account feed requires content_publish AND account
// membership (feed visibility is resolved through the publisher's user_to_account_map
// rows). Global-scope users (internal admins) are members of no account, so the backend
// publishGate refuses them; mirror that here. Feed *state* reads are vcl_view on the
// backend — this gate is deliberately narrower since state only drives the button.
const canPublishFeeds = () => {
	return hasPermission('content_publish') && !hasGlobalScope();
};

const fileImage = 'https://ts-pelican-resized.s3.amazonaws.com/y9DpT.jpg';

import { DuplicateButton, DuplicateModal } from './DuplicateComponents';
import { PublishFeedButton, PublishFeedModal } from './PublishFeedComponents';

const getSlideThumbnailPath = (slide) => {
	return getSlideThumbnailURL(slide?.file, slide?.kind);
};

class SlideDeckView extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			confirmModal:             false,
			confirmModalContent:      '',
			loading:                  false,
			slideDeck:                null,
			slideDeckName:            '',
			slideDecks:               [],
			slides:                   [],
			originalSlides:           [],
			isEditingTitle:           false,
			allContentManagerOptions: [],
			originalContentManagers:  [],
			inFolderSchedulingSlide:  null,
			folderScheduleDirtyCount: 0,
			folderScheduleSaving:     false,
			publishedFeed:            null,
		};

		// Separate controllers per loader: they run concurrently, so a shared ref
		// would be overwritten by whichever loader starts last and an abort would
		// miss the other request.
		this.abortControllerRef = React.createRef();
		this.assignAbortRef = React.createRef();
	}

	abortInFlight = () => {
		if (this.abortControllerRef.current) {
			this.abortControllerRef.current.abort();
		}
		if (this.assignAbortRef.current) {
			this.assignAbortRef.current.abort();
		}
	}

	componentDidMount() {
		this.loadAllContentManagers();
		this.loadContentManagerAssignments();
		this.loadSlideDecks();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.sideBarClickTime !== this.props.sideBarClickTime) {
			this.clearSlideDeck();
		}
		// The component instance is reused across account switches, so a mount-only
		// load leaves the previous account's decks rendered under the new account.
		if (prevProps.accountUUID !== this.props.accountUUID) {
			// Abort the previous account's in-flight requests first — a slow response
			// for the old account resolving after the new one would repaint stale decks.
			this.abortInFlight();
			this.loadAllContentManagers();
			this.loadContentManagerAssignments();
			this.loadSlideDecks();
		}
	}

	componentWillUnmount() {
		// Cleanup abort controllers on unmount
		this.abortInFlight();
	}

	loadAllContentManagers = async () => {
		const {
			app,
		} = this.props;

		const {
			accountUUID,
		} = this.props;

		app.load(async () => {
			const contentManagers = await getContentManagersByAccount(accountUUID);

			const contentManagerOptions = contentManagers.map((cm) => {
				return {
					value: cm.uuid,
					label: `${cm.last_name}, ${cm.first_name}`,
				};
			});

			return new Promise((resolve) => {
				this.setState({
					allContentManagerOptions: contentManagerOptions,
				}, resolve);
			});
		});
	}

	loadContentManagerAssignments = async () => {
		const {
			app,
		} = this.props;

		// Content managers should not see user_slide_deck data for other users
		// Skip loading this data entirely for content managers
		if (isContentManager()) {
			this.setState({
				originalContentManagers: [],
			});
			return;
		}

		app.load(async () => {
			this.assignAbortRef.current = new AbortController();

			const APIParams = {
				includes: [
					'user',
				],
				// Explicit account: the pathname-derived fallback in makeRequest is
				// stale mid account-switch (same reason as loadSlideDecks).
				...(this.props.accountUUID && {
					account_uuid: this.props.accountUUID,
				}),
			};

			let userSlideDecks;
			try {
				userSlideDecks = await API.findRecords('user_slide_deck', APIParams, this.assignAbortRef.current.signal);
			} catch (error) {
				// An account-switch abort is not an error — swallowing it here keeps
				// app.load's catch from showing the network-error modal.
				if (error.name === 'AbortError') {
					return;
				}
				throw error;
			}

			return new Promise((resolve) => {
				this.setState({
					originalContentManagers: userSlideDecks,
				}, resolve);
			});
		});
	}

	onSelectContentManagers = async (selectedOptions) => {
		const {
			accountUUID,
		} = this.props;

		const {
			originalContentManagers,
		} = this.state;

		const updatedContentManagers = selectedOptions;
		const currentSlideDeckId = this.state.slideDeck?.id;

		// Filter original content managers to only those for the current slide deck
		const currentDeckContentManagers = originalContentManagers.filter((cm) => {
			return cm.slide_deck_id === currentSlideDeckId;
		});

		// Filter for new content managers to create
		const newContentManagers = updatedContentManagers.filter((cm) => {
			return !currentDeckContentManagers.find((orgCM) => {
				return orgCM.user.uuid === cm.value;
			});
		});

		// Filter for content managers to delete (from the current deck)
		const deletedContentManagers = currentDeckContentManagers.filter((orgCM) => {
			return !updatedContentManagers.find((cm) => {
				return orgCM.user.uuid === cm.value;
			});
		});

		// Check if there are any changes
		if (newContentManagers.length === 0 && deletedContentManagers.length === 0) {
			return;
		}

		// Show loading state
		this.setState({
			loading: true,
		});

		try {
			// Execute creates
			for (const cm of newContentManagers) {
				const newUserSlideDeckData = {
					user: {
						uuid: cm.value,
					},
					slide_deck_id: this.state.slideDeck.id,
					account:       {
						uuid: accountUUID,
					},
				};

				await API.createRecord('user_slide_deck', newUserSlideDeckData);
			}

			// Execute deletes
			for (const orgCM of deletedContentManagers) {
				await API.deleteRecord('user_slide_deck', orgCM.id);
			}

			// Reload content manager assignments
			const APIParams = {
				includes: [
					'user',
				],
				// Explicit account: the pathname-derived fallback in makeRequest is
				// stale mid account-switch (same reason as loadSlideDecks).
				...(this.props.accountUUID && {
					account_uuid: this.props.accountUUID,
				}),
			};

			const userSlideDecks = await API.findRecords('user_slide_deck', APIParams);

			// Update state with new content manager assignments
			await new Promise((resolve) => {
				this.setState({
					originalContentManagers: userSlideDecks,
					loading:                 false,
				}, resolve);
			});

		} catch (error) {
			console.error('Error saving content manager assignments:', error);
			alert('Failed to save content manager assignments. Please try again.');
			this.setState({
				loading: false,
			});
		}
	}

	loadSlideDecks = async () => {
		// Create abort controller for this request
		this.abortControllerRef.current = new AbortController();

		this.setState({
			loading: true,
		});

		try {
			const response = await API.findRecords('custom::slide_deck', {
				includes: [
					'slides.gallery_file',
					'slides.slide',
					'users',
				],
				user_uuid: getSession().user,
				// Explicit: the pathname-derived account in makeRequest is stale while
				// an account switch is mid-flight, which fetched the previous account's
				// decks. Passing it here wins over the pathname fallback.
				...(this.props.accountUUID && {
					account_uuid: this.props.accountUUID,
				}),
			}, this.abortControllerRef.current.signal);

			// Check if we have a successful response with data
			if (response && response && Array.isArray(response)) {
				// Transform API data to match our expected format
				const apiSlideDecks = response
					.map((deck) => {
						return {
							uuid:      deck.uuid || deck.id,
							name:      deck.name || 'Untitled Deck',
							created:   deck.created || deck.created_at || new Date().toISOString().split('T')[0],
							slides:    deck.slides || deck.slide_count || 0,
							isChecked: false,
						};
					}).sort((a, b) => {
						return a.name.localeCompare(b.name);
					});

				this.setState({
					slideDecks: apiSlideDecks,
					loading:    false,
				});
			} else {
				console.warn('API response did not contain expected data structure');
				this.setState({
					slideDecks: [],
					loading:    false,
				});
			}
		} catch (error) {
			// Check if the error is due to abort (component unmounted)
			if (error.name === 'AbortError') {
				console.log('API request was aborted');
				return;
			}

			console.warn('Failed to load slide decks from API:', error);
			this.setState({
				slideDecks: [],
				loading:    false,
			});
		}
	}

	loadSlideDeck = async (deckId) => {
		try {
			console.log('Fetching details for slide_deck ID:', deckId);
			const response = await API.findRecord('slide_deck', deckId);
			console.log('Slide deck details response:', response);

			this.setState({
				slideDeck:     response,
				slideDeckName: response.name || 'Slide Deck Name',
			});

			this.loadPublishedFeedState(deckId);
		} catch (error) {
			console.error('Failed to fetch slide deck details:', error);
		}
	}

	loadPublishedFeedState = async (deckId) => {
		const {
			accountUUID,
		} = this.props;

		if (!canPublishFeeds()) {
			return;
		}

		try {
			const feed = await API.getPublishedFeedState(deckId, accountUUID);
			this.setState({
				publishedFeed: feed || null,
			});
		} catch (error) {
			console.error('Failed to fetch published feed state:', error);
			this.setState({
				publishedFeed: null,
			});
		}
	}

	openPublishFeedModal = () => {
		const {
			app,
		} = this.props;

		const {
			slideDeck,
			publishedFeed,
		} = this.state;

		const isPublished = !!publishedFeed;

		app.openModal({
			modalSize: 1,
			showModal: true,
			modalProps: {
				title: isPublished ? 'Unpublish Feed' : 'Publish Feed',
				customClass: 'publish-feed-modal',
				pendingChanges: false,
				jsx: (
					<PublishFeedModal
						app={app}
						onConfirm={this.performPublishToggle}
						deckName={slideDeck?.name || 'this Slide Deck'}
						isPublished={isPublished}
					/>
				),
			},
		});
	}

	performPublishToggle = async () => {
		const {
			accountUUID,
		} = this.props;

		const {
			slideDeck,
			publishedFeed,
		} = this.state;

		if (!slideDeck) {
			throw new Error('No slide deck selected.');
		}

		if (publishedFeed) {
			await API.unpublishFeed(slideDeck.id, accountUUID);
			this.setState({
				publishedFeed: null,
			});
		} else {
			const feed = await API.publishFeed(slideDeck.id, accountUUID);
			this.setState({
				publishedFeed: feed || null,
			});
		}
	}

	createNewDeck = async () => {
		const {
			accountUUID,
			app,
		} = this.props;

		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title:          'Add Slide Deck',
				customClass:    'create-new-deck',
				pendingChanges: false,
				jsx:            (
					<SlideDeckModal
						accountUUID={accountUUID}
						createOrUpdateDeck={async (deckName) => {
							try {
								const newDeckData = {
									name:    deckName,
									// Add any other required fields here
									account: {
										'uuid': accountUUID,
									},
								};

								console.log('Creating new slide deck with data:', newDeckData);

								const response = await API.createRecord('slide_deck', newDeckData);
								console.log('Create slide deck response:', response);

								// Refresh the slide decks list to show the new deck
								this.loadSlideDecks();
							} catch (error) {
								console.error('Failed to create slide deck:', error);
							}
						}}

					/>
				),
			},
		});
	}

	editDeck = async (deckID, deckName) => {
		const {
			slideDeck,
		} = this.state;

		try {
			const deckData = {
				name: deckName,
			};

			console.log('Update slide deck with data:', deckData);

			const response = await API.updateRecord('slide_deck', deckID, deckData);
			console.log('Update slide deck response:', response);

			// Refresh the slide decks list to show the new deck
			this.loadSlideDecks();

			if (slideDeck.id === deckID || slideDeck.uuid === deckID) {
				this.setState({
					slideDeck:     response,
					slideDeckName: response.name || 'Slide Deck Name',
				});
			}
		} catch (error) {
			console.error('Failed to update slide deck:', error);

			this.setState({
				confirmModal:        true,
				confirmModalContent: (
					<>
						<p>{error.toString()}</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={() => {
								this.setState({
									confirmModal:        false,
									confirmModalContent: '',
								});
							}}>OK</button>
						</div>
					</>
				),
			});
		}
	};

	startEditingTitle = () => {
		this.setState({
			isEditingTitle: true,
		});
	};

	cancelEditingTitle = () => {
		const {
			slideDeck,
		} = this.state;

		this.setState({
			isEditingTitle: false,
			slideDeckName:  slideDeck.name || '',
		});
	};

	handleTitleChange = (e) => {
		this.setState({
			slideDeckName: e.target.value,
		});
	};

	saveTitleEdit = () => {
		const {
			slideDeckName,
			slideDeck,
		} = this.state;


		this.setState({
			isEditingTitle: false,
		});

		if (slideDeck.name === slideDeckName) {
			return;
		}

		this.editDeck(slideDeck.id, slideDeckName);
	};

	handleTitleKeyPress = (e) => {
		if (e.key === 'Enter') {
			this.saveTitleEdit();
		} else if (e.key === 'Escape') {
			this.cancelEditingTitle();
		}
	};

	deleteDeck = async (deckUUID) => {
		try {
			const response = await API.deleteRecord('slide_deck', deckUUID);
			console.log('Create slide deck response:', response);
		} catch (error) {
			console.error('Failed to delete slide deck via API:', error);
			this.setState({
				confirmModalContent: <>
					<p>{error.toString()}</p>
					<div className="confirm-modal-buttons">
						<button className="button" onClick={() => {
							this.setState({
								confirmModal:        false,
								confirmModalContent: '',
							});
						}}>OK</button>
					</div>
				</>,
			});

			return;
		}

		// Perform deletion
		const {
			slideDecks,
		} = this.state;
		const newSlideDecks = slideDecks.filter((d) => {
			return d.uuid !== deckUUID;
		});

		this.setState({
			slideDecks:          newSlideDecks,
			confirmModal:        false,
			confirmModalContent: '',
		});
	}

	deleteSingleDeck = (deckUUID) => {
		const {
			accountUUID,
		} = this.props;

		const {
			slideDecks,
		} = this.state;

		const deck = slideDecks.find((d) => {
			return d.uuid === deckUUID;
		});

		slideDeckDeleteValidation(deckUUID, accountUUID)
			.then((vclCount) => {
				if (vclCount === 0) {
					this.setState({
						confirmModal:        true,
						confirmModalContent: (
							<>
								<p>{`Delete slide deck "${deck.name}"? This action cannot be undone.`}</p>
								<div className="confirm-modal-buttons">
									<button className="button" data-test-id="confirmModalDeleteButton" onClick={async () => {
										this.deleteDeck(deckUUID);
									}}>Delete</button>
									<button className="button" onClick={() => {
										this.setState({
											confirmModal:        false,
											confirmModalContent: '',
										});
									}}>Cancel</button>
								</div>
							</>
						),
					});
				} else {
					this.setState({
						confirmModal:        true,
						confirmModalContent: (
							<>
								<p>{`${deck.name} is assigned to ${vclCount === 1 ? 'a Visual Content Loop' : `${vclCount} Visual Content Loops`}.`}</p>
								<p>{`Please remove ${deck.name} from ${vclCount === 1 ? 'that Visual Content Loop' : 'those Visual Content Loops'}.`}</p>
								<div className="confirm-modal-buttons">
									<button className="button" onClick={() => {
										this.setState({
											confirmModal:        false,
											confirmModalContent: '',
										});
									}}>OK</button>
								</div>
							</>
						),
					});
				}
			})
			.catch((error) => {
				this.setState({
					confirmModal:        true,
					confirmModalContent: (
						<>
							<p>{`Network error, please try again later.`}</p>
							<p>{`${error}`}</p>
							<div className="confirm-modal-buttons">
								<button className="button" onClick={() => {
									this.setState({
										confirmModal:        false,
										confirmModalContent: '',
									});
								}}>OK</button>
							</div>
						</>
					),
				});
			});
	}

	deleteSelectedDecks = (selectedDecks) => {
		const {
			accountUUID,
		} = this.props;

		const {
			slideDecks,
		} = this.state;

		if (selectedDecks.length === 0) {
			return;
		}

		Promise.all(selectedDecks.map((deckUUID) => {
			return slideDeckDeleteValidation(deckUUID, accountUUID);
		}))
			.then((results) => {
				const deletionBlocked = results.find((count) => {
					return count > 0;
				});

				if (deletionBlocked) {
					throw new Error('One or more selected slide decks are assigned to Visual Content Loops. Please remove those assignments before deleting the slide decks.');
				}

				this.setState({
					confirmModal:        true,
					confirmModalContent: (
						<>
							<p>Deleting slide decks will affect your Visual Content Loop presentations. Would you like to proceed?</p>
							<div className="confirm-modal-buttons">
								<button className="button" data-test-id="confirmModalProceedButton" onClick={() => {
									let errorMsgs = [];

									const deletePromises = selectedDecks.map(async (deckUUID) => {
										try {
											const response = await API.deleteRecord('slide_deck', deckUUID);
											console.log('Delete slide deck response:', response);
										} catch (error) {
											errorMsgs.push(error.toString());
											console.error('Failed to delete slide deck via API:', error);
											throw error;
										}
									});


									Promise.allSettled(deletePromises).then(() => {
										// Perform deletion
										if (errorMsgs.length === 0) {
											const newSlideDecks = slideDecks.filter((deck) => {
												return !selectedDecks.includes(String(deck.uuid));
											});

											this.setState({
												slideDecks:          newSlideDecks,
												confirmModal:        false,
												confirmModalContent: '',
											}, () => {
												this.loadSlides();
											});
										} else if (errorMsgs.length < 3) {
											this.setState({
												confirmModalContent: <>
													{errorMsgs.map((error, index) => {
														return (
															<p key={index}>{error}</p>
														);
													})}
													<div className="confirm-modal-buttons">
														<button className="button" onClick={() => {
															this.setState({
																confirmModal:        false,
																confirmModalContent: '',
															});
														}}>OK</button>
													</div>
												</>,
											});
										} else {
											this.setState({
												confirmModalContent: <>
													<p>{errorMsgs.length} errors found.</p>
													<p>{selectedDecks.length - errorMsgs.length} successes.</p>
													<div className="confirm-modal-buttons">
														<button className="button" onClick={() => {
															this.setState({
																confirmModal:        false,
																confirmModalContent: '',
															});
														}}>OK</button>
													</div>
												</>,
											});
										}
									});
								}}>Proceed</button>
								<button className="button" onClick={() => {
									this.setState({
										confirmModal:        false,
										confirmModalContent: '',
									});
								}}>Cancel</button>
							</div>
						</>
					),
				});
			})
			.catch((error) => {
				this.setState({
					confirmModal:        true,
					confirmModalContent: (
						<>
							<p>{`${error}`}</p>
							<div className="confirm-modal-buttons">
								<button className="button" onClick={() => {
									this.setState({
										confirmModal:        false,
										confirmModalContent: '',
									});
								}}>OK</button>
							</div>
						</>
					),
				});
			});
	}

	loadSlides = async () => {
		const {
			slideDeck,
		} = this.state;

		const {
			accountUUID,
			fd,
		} = this.props;

		const isInFolderSchedulingEnabled = fd
			? !!(fd[`account.${accountUUID}`]?.is_in_folder_scheduling_enabled)
			: false;

		this.abortControllerRef.current = new AbortController();

		try {
			const includes = [
				'slides.folder',
				'slides.galleryFile',
				'slides.slide',
			];

			if (isInFolderSchedulingEnabled) {
				includes.push('slides.folder.files');
			}

			const response = await API.findRecord('slideDeck', slideDeck.id, {
				includes,
			}, this.abortControllerRef.current.signal);

			const nextSlides = (response.slides || [])
				.map((slideDeckSlide) => {
					const baseSlideData = {
						slideDeckSlideID: slideDeckSlide.id,
						order:            slideDeckSlide.order,
						folderSlots:      slideDeckSlide.folder_slots,
						duration:         slideDeckSlide.duration,
						scheduled:        slideDeckSlide.scheduled,
						scheduling:       slideDeckSlide.scheduling || {},
					};

					if (slideDeckSlide.gallery_file_id) {
						const {
							gallery_file = {},
						} = slideDeckSlide;

						return {
							...gallery_file,
							...baseSlideData,
							description:        gallery_file.description || gallery_file.name,
							src:                getThumbnailPath(gallery_file.path),
							kind:               gallery_file.mime_type,
							isLibrarySelection: true,
						};
					} else if (slideDeckSlide.folder_id) {
						const {
							folder = {},
						} = slideDeckSlide;

						return {
							...folder,
							...baseSlideData,
							src:                fileImage,
							file_name:          folder.name,
							description:        folder.label,
							kind:               'folder',
							isLibrarySelection: true,
						};
					} else if (slideDeckSlide.slide_id) {
						const {
							slide = {},
						} = slideDeckSlide;

						const isURLSlide = slide.kind === 'html';

						return {
							...slide,
							...baseSlideData,
							description: slide.description || slide.name,
							src:         isURLSlide ? getURLSlideThumbnailPath(slide.file) : getSlideThumbnailPath(slide),
							fallbackSrc: isURLSlide ? urlSlideFallbackIcon : undefined,
							file_name:   slide.name,
							slideID:     slide.id,
						};
					}

					return baseSlideData;
				})
				.sort((a, b) => {
					return a.order - b.order;
				});

			this.setState({
				slides:            nextSlides,
				originalSlides:    nextSlides,
				filteredRowsCount: nextSlides.length,
			});
		} catch (error) {
			console.error('Failed to fetch slide deck details:', error);
		}
	}

	addSlideModal = async () => {
		const {
			accountUUID,
			app,
			fd,
		} = this.props;

		const {
			slideDeck,
		} = this.state;

		app.openModal({
			modalSize:  2,
			showModal:  true,
			modalProps: {
				title:          'Add Slide',
				customClass:    'create-new-slide',
				pendingChanges: false,
				jsx:            (
					<SlideModal
						isEdit={false}
						accountUUID={accountUUID}
						app={app}
						slideDeck={slideDeck}
						addSlide={this.addSlide}
						allowSlideshowUrl={!!getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`)}
					/>
				),
			},
		});
	}

	editSlideModal = async (slideData) => {
		const {
			accountUUID,
			app,
			fd,
		} = this.props;

		app.openModal({
			modalSize:  2,
			showModal:  true,
			modalProps: {
				title:          'Edit Slide',
				customClass:    'edit-slide',
				pendingChanges: false,
				jsx:            (
					<SlideModal
						isEdit={true}
						app={app}
						accountUUID={accountUUID}
						slideData={slideData}
						deleteSlide={this.deleteSlide}
						editSlide={this.editSlide}
						allowSlideshowUrl={!!getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`)}
					/>
				),
			},
		});
	}

	addSlide = async (slideData, isLibrarySelection) => {
		const {
			accountUUID,
			app,
		} = this.props;

		const {
			slideDeck,
			slides,
		} = this.state;

		let newSlideDeckSlideData;

		try {
			if (isLibrarySelection) {
				newSlideDeckSlideData = {
					folder_id:       slideData.folderID || null,
					gallery_file_id: slideData.galleryFileID || null,
					slide_deck_id:   slideDeck.id || null,
					duration:        slideData.duration,
					scheduled:       slideData.scheduled,
					scheduling:      slideData.scheduling,
					order:           slideData.order,
					folder_slots:    slideData.folderID ? slideData.folderSlots : 1,
					account:         {
						'uuid': accountUUID,
					},
				};

				const slideDeckSlideResponse = await API.createRecord('slide_deck_slide', newSlideDeckSlideData);

				newSlideDeckSlideData.slideDeckSlideID = slideDeckSlideResponse.id;

				console.log('Create slide deck slide response:', slideDeckSlideResponse);
			} else if (slideData.kind === 'html') {
				// URL slide (DEV-12028): nothing to upload — the https URL is the
				// slide's `file`, rendered in a sandboxed iframe by Condor's Pelican.
				// Create the slide row, then the slide_deck_slide pointing at it
				// (deck-side counterpart of Slideshow.addIframe).
				const url = (slideData.filePath || '').trim();
				const newSlideData = {
					account: {
						'uuid': accountUUID,
					},
					file:        url,
					kind:        'html',
					// slide.name is varchar(255); description is free text
					name:        (slideData.description || url).slice(0, URL_SLIDE_MAX_LENGTH),
					description: slideData.description || url,
					duration:    slideData.duration,
					file_size:   0,
					resolution:  '',
				};

				const slideResponse = await API.createRecord('slide', newSlideData);
				console.log('Create slide (url) response:', slideResponse);

				newSlideDeckSlideData = {
					slide_id:      slideResponse.id,
					slide_deck_id: slideDeck.id || null,
					duration:      slideData.duration,
					scheduled:     slideData.scheduled,
					scheduling:    slideData.scheduling,
					order:         slideData.order,
					folder_slots:  1,
					account:       {
						'uuid': accountUUID,
					},
				};

				let slideDeckSlideResponse;
				try {
					slideDeckSlideResponse = await API.createRecord('slide_deck_slide', newSlideDeckSlideData);
				} catch (err) {
					// Two REST calls, no server transaction: don't strand the slide row.
					await API.deleteRecord('slide', slideResponse.id).catch((cleanupErr) => {
						console.error('Failed to clean up orphaned url slide:', slideResponse.id, cleanupErr);
					});
					throw err;
				}

				newSlideDeckSlideData.slideDeckSlideID = slideDeckSlideResponse.id;

				console.log('Create slide deck slide (url) response:', slideDeckSlideResponse);
			} else {
				// Upload + create slide + create slide_deck_slide in ONE server
				// transaction, so the DB writes can't mispoint a slide. (The S3
				// object is uploaded before commit, so a failure can still leave an
				// unreferenced object in the uploads library — a benign asset.)
				const {
					slide_deck_slide: createdSDS,
				} = await createSlideDeckSlideWithUpload(
					slideData.file,
					slideData.filePath,
					accountUUID,
					slideDeck.id,
					{
						description:  slideData.description,
						duration:     slideData.duration,
						scheduled:    slideData.scheduled,
						scheduling:   slideData.scheduling,
						order:        slideData.order,
						folder_slots: 1,
					}
				);

				newSlideDeckSlideData = {
					slideDeckSlideID: createdSDS.id,
					slide_id:         createdSDS.slide_id,
					slide_deck_id:    createdSDS.slide_deck_id,
					duration:         createdSDS.duration,
					scheduled:        createdSDS.scheduled,
					scheduling:       createdSDS.scheduling,
					order:            createdSDS.order,
					folder_slots:     createdSDS.folder_slots,
				};
			}

			this.resetOrder(slides, newSlideDeckSlideData);
		} catch (error) {
			console.error('Failed to create slide deck:', error);
		} finally {
			app.closeModal();
		}
	}

	editSlide = async (updatedSlideData, isLibrarySelection) => {
		const {
			app,
			accountUUID,
		} = this.props;

		const {
			slides,
		} = this.state;

		try {
			const replacedFile = !!updatedSlideData.replacedFile;

			const hasScheduling = updatedSlideData.scheduling && JSON.stringify(updatedSlideData.scheduling) !== '{}';
			let slideDeckSlideData = {
				duration:     updatedSlideData.duration,
				order:        updatedSlideData.order,
				folder_slots: updatedSlideData.folderSlots,
				scheduled:    hasScheduling && updatedSlideData.scheduled,
				scheduling:   updatedSlideData.scheduling,
			};

			if (replacedFile) {
				if (isLibrarySelection) {
					if (updatedSlideData.galleryFileID) {
						slideDeckSlideData.gallery_file_id = updatedSlideData.galleryFileID;
						slideDeckSlideData.folder_id = null;
						slideDeckSlideData.slide_id = null;
					} else if (updatedSlideData.folderID) {
						slideDeckSlideData.folder_id = updatedSlideData.folderID;
						slideDeckSlideData.folder_slots = updatedSlideData.folderSlots || 1;
						slideDeckSlideData.gallery_file_id = null;
						slideDeckSlideData.slide_id = null;
					}
					const slideDeckSlideResponse = await API.updateRecord('slide_deck_slide', updatedSlideData.slideDeckSlideID, slideDeckSlideData);
					console.log('Update slide deck (replacement - library) response:', slideDeckSlideResponse);
				} else {
					try {
						if (updatedSlideData.file) {
							// Atomic: upload + new slide + repoint the existing
							// slide_deck_slide at it, in one server transaction.
							const {
								slide_deck_slide: updatedSDS,
							} = await createSlideDeckSlideWithUpload(
								updatedSlideData.file,
								updatedSlideData.filePath,
								accountUUID,
								this.state.slideDeck.id,
								{
									description:         updatedSlideData.description,
									duration:            updatedSlideData.duration,
									scheduled:           slideDeckSlideData.scheduled,
									scheduling:          slideDeckSlideData.scheduling,
									order:               slideDeckSlideData.order,
									folder_slots:        slideDeckSlideData.folder_slots,
									slide_deck_slide_id: updatedSlideData.slideDeckSlideID,
								}
							);
							console.log('Replacement upload (atomic) response:', updatedSDS);
						} else {
							// Degenerate replace with no new file: preserve prior behavior.
							const newSlideData = {
								account: {
									'uuid': this.props.accountUUID,
								},
								kind:        updatedSlideData.kind,
								name:        updatedSlideData.fileName,
								description: updatedSlideData.description,
								duration:    updatedSlideData.duration,
								file_size:   updatedSlideData.fileSize,
								resolution:  updatedSlideData.resolution,
							};

							const slideResponse = await API.createRecord('slide', newSlideData);
							console.log('Create slide (replacement, no file) response:', slideResponse);

							slideDeckSlideData.slide_id = slideResponse.id;
							slideDeckSlideData.gallery_file_id = null;
							slideDeckSlideData.folder_id = null;

							const slideDeckSlideResponse = await API.updateRecord('slide_deck_slide', updatedSlideData.slideDeckSlideID, slideDeckSlideData);
							console.log('Update slide deck (replacement, no file) response:', slideDeckSlideResponse);
						}
					} catch (err) {
						console.error('Replacement upload flow failed:', err);
						throw err;
					}
				}
			} else {
				const slideDeckSlideResponse = await API.updateRecord('slide_deck_slide', updatedSlideData.slideDeckSlideID, slideDeckSlideData);
				console.log('Update slide deck response:', slideDeckSlideResponse);

				if (!isLibrarySelection) {
					const newSlideData = {
						description: updatedSlideData.description,
						duration:    updatedSlideData.duration,
					};

					if (updatedSlideData.kind === 'html') {
						// URL slides keep the (editable) URL in filePath — SlideModal
						// has already enforced https before calling us.
						newSlideData.file = (updatedSlideData.filePath || '').trim();
						newSlideData.name = (updatedSlideData.description || newSlideData.file).slice(0, URL_SLIDE_MAX_LENGTH);
					}

					const slideResponse = await API.updateRecord('slide', updatedSlideData.id, newSlideData);

					console.log('Update slide response:', slideResponse);
				}
			}

			this.resetOrder(slides, updatedSlideData);
		} catch (error) {
			console.error('Failed to update slide deck:', error);
		} finally {
			app.closeModal();
		}
	}

	deleteSlide = async (slideID, slideDeckSlideID) => {
		const {
			app,
		} = this.props;

		const {
			slides,
		} = this.state;

		let shouldLoadSlides = false;

		try {
			if (slideID) {
				await API.deleteRecord('slide', slideID);
				shouldLoadSlides = true;
			}

			await API.deleteRecord('slide_deck_slide', slideDeckSlideID);
			shouldLoadSlides = true;
		} catch (error) {
			console.error('Failed to update slide deck:', error);
		} finally {
			if (shouldLoadSlides) {
				const slidesWithoutDeleted = slides.filter((s) => {
					return s.slideDeckSlideID !== slideDeckSlideID;
				});
				this.resetOrder(slidesWithoutDeleted);
			}

			app.closeModal();
		}
	}

	deleteSingleSlide = async (slide, slideDeckSlideID) => {
		this.setState({
			confirmModal:        true,
			confirmModalContent: (
				<>
					<p>{`Delete slide "${slide.description}"? This action cannot be undone.`}</p>
					<div className="confirm-modal-buttons">
						<button className="button" data-test-id="confirmModalDeleteButton" onClick={async () => {
							this.deleteSlide(slide.slideID, slideDeckSlideID);
							this.setState({
								confirmModal:        false,
								confirmModalContent: '',
							});
						}}>Delete</button>
						<button className="button" onClick={() => {
							this.setState({
								confirmModal:        false,
								confirmModalContent: '',
							});
						}}>Cancel</button>
					</div>
				</>
			),
		});
	}

	deleteSelectedSlides = (selectedSlideIds) => {
		const {
			slides,
		} = this.state;

		if (!selectedSlideIds || selectedSlideIds.length === 0) return;

		const selectedSet = new Set(selectedSlideIds.map(String));
		const selectedSlides = slides.filter((s) => {
			const idsToCheck = [];
			if (s.slideDeckSlideID) idsToCheck.push(`sds-${s.slideDeckSlideID}`);
			if (s.slideID) idsToCheck.push(`s-${s.slideID}`);
			if (s.file_name) idsToCheck.push(`f-${s.file_name}`);
			return idsToCheck.some((id) => {
				return selectedSet.has(id);
			});
		});

		if (selectedSlides.length === 0) return;

		this.setState({
			confirmModal:        true,
			confirmModalContent: (
				<>
					<p>Delete {selectedSlides.length} selected slide{selectedSlides.length !== 1 && 's'}? This action cannot be undone.</p>
					<div className="confirm-modal-buttons">
						<button className="button" data-test-id="confirmModalProceedButton" onClick={() => {
							let errorMsgs = [];

							const deletePromises = selectedSlides.map(async (slideObj) => {
								try {
									if (slideObj.slideID) {
										await API.deleteRecord('slide', slideObj.slideID);
									}
									await API.deleteRecord('slide_deck_slide', slideObj.slideDeckSlideID);
								} catch (error) {
									console.error('Failed to delete slide via API:', error);
									errorMsgs.push(error.toString());
								}
							});

							Promise.allSettled(deletePromises).then(() => {
								if (errorMsgs.length === 0) {
									const remainingSlides = slides.filter((s) => {
										return !selectedSlides.find((sel) => {
											return sel.slideDeckSlideID === s.slideDeckSlideID;
										});
									});
									this.setState({
										confirmModal:        false,
										confirmModalContent: '',
									}, () => {
										this.resetOrder(remainingSlides);
									});
								} else if (errorMsgs.length < 3) {
									this.setState({
										confirmModalContent: <>
											{errorMsgs.map((e, i) => {
												return <p key={i}>{e}</p>;
											})}
											<div className="confirm-modal-buttons">
												<button className="button" onClick={() => {
													this.setState({
														confirmModal:        false,
														confirmModalContent: '',
													});
												}}>OK</button>
											</div>
										</>,
									});
								} else {
									this.setState({
										confirmModalContent: <>
											<p>{errorMsgs.length} errors found.</p>
											<p>{selectedSlides.length - errorMsgs.length} successes.</p>
											<div className="confirm-modal-buttons">
												<button className="button" onClick={() => {
													this.setState({
														confirmModal:        false,
														confirmModalContent: '',
													});
												}}>OK</button>
											</div>
										</>,
									});
								}
							});
						}}>Proceed</button>
						<button className="button" onClick={() => {
							this.setState({
								confirmModal:        false,
								confirmModalContent: '',
							});
						}}>Cancel</button>
					</div>
				</>
			),
		});
	}

	resetOrder = async (slides, newSlide) => {
		const oldSlides = slides
			.filter((s) => {
				return s.slideDeckSlideID !== newSlide?.slideDeckSlideID;
			})
			.map((slide, index) => {
				return {
					...slide,
					order: index + 1.5,
				};
			})
			.sort((a, b) => {
				return a.order - b.order;
			});

		const updatedSlides = [
			newSlide,
			...oldSlides,
		]
			.filter((slide) => {
				return slide?.slideDeckSlideID;
			})
			.sort((a, b) => {
				return a.order - b.order;
			}).map((slide, index) => {
				return {
					...slide,
					order: index + 1,
				};
			});

		await Promise.all(updatedSlides.map(async (s) => {
			const updatedData = {
				order: s.order,
			};

			return API.updateRecord('slide_deck_slide', s.slideDeckSlideID, updatedData);
		}));

		// Update the state with the reordered slides
		this.loadSlides();
	}

	onReorder = (oldIndex, newIndex) => {
		const {
			app,
		} = this.props;

		const {
			slides,
			originalSlides,
		} = this.state;

		// Create a copy of the slides array
		const reorderedSlides = [ ...slides, ];
		// Remove the dragged item from its old position
		const [ draggedSlide, ] = reorderedSlides.splice(oldIndex, 1);

		// Insert the dragged item at its new position
		reorderedSlides.splice(newIndex, 0, draggedSlide);

		// Update the order property for all slides to reflect their new positions
		const updatedSlides = reorderedSlides
			.map((slide, index) => {
				return {
					...slide,
					order: index + 1, // Assuming order starts from 1
				};
			})
			.sort((a, b) => {
				return a.order - b.order;
			});

		// Update the state with the reordered slides
		this.setState({
			slides: updatedSlides,
		});

		if (JSON.stringify(slides) === JSON.stringify(updatedSlides)) {
			return;
		}

		if (JSON.stringify(originalSlides) === JSON.stringify(updatedSlides)) {
			app.clearPendingChanges();
			return;
		}

		const newPromises = updatedSlides.map((s) => {
			const updatedData = {
				order: s.order,
				id:	   s.slideDeckSlideID,
			};

			return async () => {
				const slideDeckSlideResponse = await API.updateRecord('slide_deck_slide', updatedData.id, updatedData);
				console.log('Update slide deck response:', slideDeckSlideResponse);
			};
		});

		app.queuePendingPromises('slideDeckSlide', newPromises);
	}

	renderViewWindowColumns = () => {
		return (<>
			{[].map((column) => {
				return (<>
					<div key={column.id}>
						{column.Header}
					</div>
				</>);
			})}
		</>);
	}

	openFolderScheduling = (folderSlideRaw) => {
		this._folderScheduleSaveRef = { current: null };
		this.setState({
			inFolderSchedulingSlide:  folderSlideRaw,
			folderScheduleDirtyCount: 0,
			folderScheduleSaving:     false,
		});
	};

	closeFolderScheduling = () => {
		this._folderScheduleSaveRef = null;
		this.setState({
			inFolderSchedulingSlide:  null,
			folderScheduleDirtyCount: 0,
			folderScheduleSaving:     false,
		});
	};

	handleFolderScheduleDirtyChange = (dirtyCount, saving) => {
		this.setState({
			folderScheduleDirtyCount: dirtyCount,
			folderScheduleSaving:     saving,
		});
	};

	clearSlideDeck = () => {
		const {
			app,
		} = this.props;

		this.setState({
			confirmModal:        false,
			confirmModalContent: '',
			loading:             false,
			slideDeck:           null,
			slideDeckName:       '',
			slides:              [],
			isEditingTitle:      false,
			publishedFeed:       null,
		});

		app.clearPendingChanges();
	}

	openDuplicateModal = () => {
		const {
			app,
		} = this.props;

		app.openModal({
			modalSize: 1,
			showModal: true,
			modalProps: {
				title: 'Duplicate Slide Deck',
				customClass: 'duplicate-slide-deck-modal',
				pendingChanges: false,
				jsx: (
					<DuplicateModal
						app={app}
						onDuplicate={this.performDuplicate}
						itemName="Slide Deck"
					/>
				),
			},
		});
	}

	performDuplicate = async (count) => {
		const {
			slideDeck,
			slideDecks,
		} = this.state;

		const {
			accountUUID,
		} = this.props;

		if (!slideDeck) {
			throw new Error('No slide deck selected to duplicate.');
		}

		const sourceName = slideDeck.name || 'Slide Deck';

		const takenNames = new Set((slideDecks || []).map((d) => d.name || ''));

		const getUniqueName = (base) => {
			if (!takenNames.has(base)) {
				takenNames.add(base);
				return base;
			}
			let idx = 2;
			while (takenNames.has(`${base} ${idx}`)) {
				idx++;
			}
			const uniqueName = `${base} ${idx}`;
			takenNames.add(uniqueName);
			return uniqueName;
		};

		const base = `${sourceName} Copy`;

		const response = await API.findRecord('slide_deck', slideDeck.id, {
			includes: ['slides.slide'],
		});

		const originalSlides = response.slides || [];
		const sortedSlides = [...originalSlides].sort((a, b) => (a.order || 0) - (b.order || 0));

		for (let i = 0; i < count; i++) {
			const newDeckName = getUniqueName(base);

			let newDeck = null;
			const createdSlideIds = [];

			try {
				newDeck = await API.createRecord('slide_deck', {
					name: newDeckName,
					account: { uuid: accountUUID },
				});

				if (sortedSlides.length > 0) {
					for (const slide of sortedSlides) {
						const newSlideData = {
							slide_deck_id: newDeck.id,
							account: { uuid: accountUUID },
							duration: slide.duration,
							scheduled: slide.scheduled,
							scheduling: slide.scheduling,
							order: slide.order,
							folder_slots: slide.folder_slots,
						};

						if (slide.slide_id && slide.slide) {
							const newUploadedSlideData = {
								account: { uuid: accountUUID },
								file: slide.slide.file,
								kind: slide.slide.kind,
								name: slide.slide.name,
								description: slide.slide.description,
								duration: slide.slide.duration,
								file_size: slide.slide.file_size,
								resolution: slide.slide.resolution,
							};
							const duplicatedSlide = await API.createRecord('slide', newUploadedSlideData);
							createdSlideIds.push(duplicatedSlide.id);
							newSlideData.slide_id = duplicatedSlide.id;
						} else if (slide.slide_id) {
							newSlideData.slide_id = slide.slide_id;
						}
						if (slide.gallery_file_id) {
							newSlideData.gallery_file_id = slide.gallery_file_id;
						}
						if (slide.folder_id) {
							newSlideData.folder_id = slide.folder_id;
						}

						await API.createRecord('slide_deck_slide', newSlideData);
					}
				}
			} catch (err) {
				if (newDeck) {
					try {
						await API.deleteRecord('slide_deck', newDeck.id);
					} catch (cleanupErr) {
						console.error('Failed to cleanup duplicated slide deck:', cleanupErr);
					}
				}
				for (const slideId of createdSlideIds) {
					try {
						await API.deleteRecord('slide', slideId);
					} catch (cleanupErr) {
						console.error(`Failed to cleanup duplicated slide ${slideId}:`, cleanupErr);
					}
				}
				throw err;
			}
		}

		this.clearSlideDeck();
		this.loadSlideDecks();
	}


	render() {
		const {
			accountUUID,
			app,
			fd,
		} = this.props;

		const {
			confirmModal,
			confirmModalContent,
			loading,
			slideDecks,
			slideDeck,
			slides,
			isEditingTitle,
			slideDeckName,
			allContentManagerOptions,
			originalContentManagers,
			inFolderSchedulingSlide,
			folderScheduleDirtyCount,
			folderScheduleSaving,
			publishedFeed,
		} = this.state;

		const canPublishFeed = canPublishFeeds() && (fd
			? !!(fd[`account.${accountUUID}`]?.allow_feed_publishing)
			: false);

		const isInFolderSchedulingEnabled = fd
			? !!(fd[`account.${accountUUID}`]?.is_in_folder_scheduling_enabled)
			: false;

		const hasPendingChanges = app.hasPendingPromises();

		// Calculate if all filtered rows are selected
		let confirmModalElement = '';
		if (confirmModal) {
			confirmModalElement = (
				<div className="confirm-modal-container">
					<div className="confirm-modal-content">
						{confirmModalContent}
					</div>
				</div>
			);
		}

		return (
			<div className="systems-page fade-in">
				{confirmModalElement}

				<div className={styles.pageHeader}>
					{slideDeck && (
						isEditingTitle && !inFolderSchedulingSlide ? (
							<div>
								<h2
									style={{
										margin:     0,
										display:    'inline-flex',
										alignItems: 'center',
										gap:        '8px',
									}}
								>
									Slide Deck:
								</h2>
								<input
									type="text"
									value={slideDeckName}
									onChange={this.handleTitleChange}
									onKeyDown={this.handleTitleKeyPress}
									onBlur={this.saveTitleEdit}
									autoFocus
									style={{
										fontSize:     '1.5rem',
										fontWeight:   'bold',
										border:       '2px solid #007cba',
										borderRadius: '4px',
										padding:      '4px 8px',
										background:   '#fff',
										outline:      'none',
										marginLeft:   '10px',
									}}
								/>
							</div>
						) : inFolderSchedulingSlide ? (
							<h2 style={{ margin: 0 }}>
								{`${inFolderSchedulingSlide.label || inFolderSchedulingSlide.description || 'Folder'} IN-FOLDER SCHEDULING`}
							</h2>
						) : (
							<h2>
								Slide Deck:
								<span
									onClick={this.startEditingTitle}
									title='Click to edit slide deck name'
									style={{
										cursor:     'pointer',
										marginLeft: '10px',
										display:    'inline-flex',
										alignItems: 'center',
										gap:        '8px',
									}}
								>
									{slideDeck.name}
									<CustomIcon
										icon='edit'
										style={{
											fontSize: '0.8rem',
											opacity:  0.6,
										}}
									/>
								</span>
							</h2>
						)
					)}
					{!slideDeck && (
						<h2>Slide Decks</h2>
					)}
					{slideDeck && (() => {
						const inFolder  = !!inFolderSchedulingSlide;
						const isDisabled = inFolder
							? (folderScheduleDirtyCount === 0 || folderScheduleSaving)
							: !hasPendingChanges;
						const label = inFolder
							? (folderScheduleSaving
								? 'Saving…'
								: folderScheduleDirtyCount > 0
									? `Save (${folderScheduleDirtyCount})`
									: 'Save'
							)
							: 'SAVE';
						return (
							<div className={styles.pageHeaderButtons}>
								{canPublishFeed && !inFolder && (
									<PublishFeedButton
										onOpenModal={this.openPublishFeedModal}
										hasChanges={hasPendingChanges}
										handleSave={() => app.runPendingPromises()}
										isPublished={!!publishedFeed}
									/>
								)}
								<DuplicateButton
									onOpenModal={this.openDuplicateModal.bind(this)}
									hasChanges={hasPendingChanges}
									handleSave={() => app.runPendingPromises()}
									itemName="Slide Deck"
								/>
								<button
									type="button"
									className={`button ${isDisabled ? 'button-disabled' : ''}`}
									disabled={isDisabled}
									onClick={() => {
										if (inFolder) {
											this._folderScheduleSaveRef?.current?.save?.();
										} else {
											app.saveDataAndCloseModal('slide_deck', this.loadSlides);
										}
									}}
									style={{
										display:    'flex',
										alignItems: 'center',
										gap:        '5px',
									}}
								>
									<CustomIcon icon="save" />
									{label}
								</button>
							</div>
						);
					})()}
				</div>

				{/* Loading Indicator */}
				{loading && (
					<div style={{
						display:        'flex',
						justifyContent: 'center',
						alignItems:     'center',
						padding:        '40px',
						fontSize:       '16px',
						color:          '#666',
					}}>
						<CustomIcon icon="spinner" style={{
							marginRight: '8px',
						}} />
						Loading slide decks...
					</div>
				)}

				{!loading && (
					<div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
						{!slideDeck ? (
							<SlideDeckTableView
								accountUUID={accountUUID}
								app={app}
								slideDecks={slideDecks}
								loadSlideDeck={this.loadSlideDeck}
								createNewDeck={this.createNewDeck}
								deleteSingleDeck={this.deleteSingleDeck}
								deleteSelectedDecks={this.deleteSelectedDecks}
							/>
						) : inFolderSchedulingSlide ? (
							<InFolderScheduling
								app={app}
								accountUUID={accountUUID}
								slideDeck={slideDeck}
								folderSlide={inFolderSchedulingSlide}
								onBack={this.closeFolderScheduling}
								onBackToList={this.clearSlideDeck}
								saveRef={this._folderScheduleSaveRef}
								onDirtyChange={this.handleFolderScheduleDirtyChange}
								isInFolderSchedulingEnabled={isInFolderSchedulingEnabled}
							/>
						) : (
							<div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
								<SlideDeckSlidesTableView
									accountUUID={accountUUID}
									app={app}
									slides={slides}
									slideDeck={slideDeck}
									originalContentManagers={originalContentManagers}
									contentManagerOptions={allContentManagerOptions}
									onSelectContentManagers={this.onSelectContentManagers}
									loadSlides={this.loadSlides}
									addSlideModal={this.addSlideModal}
									editSlideModal={this.editSlideModal}
									deleteSingleSlide={this.deleteSingleSlide}
									deleteSelectedSlides={this.deleteSelectedSlides}
									onReorder={this.onReorder}
									renderViewWindowColumns={this.renderViewWindowColumns}
									activateViewWindow={this.activateViewWindow}
									clearSlideDeck={this.clearSlideDeck}
									loadSlideDecks={this.loadSlideDecks}
									isInFolderSchedulingEnabled={isInFolderSchedulingEnabled}
									openFolderScheduling={this.openFolderScheduling}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}

if (!window.CrowFormField) {
	window.CrowFormField = {};
}
window.CrowFormField['custom.vulture.slide_deck'] = SlideDeckView;
export default SlideDeckView;
